import { loadQuery } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";

import { hasAuthSession } from "app/utils/auth";
import { RelayEnvironment } from "RelayEnvironment";

import type { PreloadedQuery } from "react-relay";
import type {
  FetchPolicy,
  GraphQLTaggedNode,
  IEnvironment,
  OperationType,
  VariablesOf,
} from "relay-runtime";

/**
 * A query a route loader has started fetching, handed to the page as its
 * loader data.
 */
export interface PreloadedRoute<TQuery extends OperationType> {
  /** Reference to pass to `usePreloadedQuery` (via `useRoutePreloadedQuery`). */
  queryRef: PreloadedQuery<TQuery>;
  /**
   * The variables the reference was loaded with, serialised, so the page can
   * tell when the variables it wants have drifted from the ones in flight.
   */
  variablesKey: string;
}

/**
 * Relay's completed form of a query's variables: an entry for every variable
 * the query declares, in declaration order, with defaults applied and nullish
 * values normalised.
 *
 * `loadQuery` records the operation under this form but sends the variables it
 * was given to the network as they are, and relay-test-utils matches a mocked
 * response by comparing the two — so a query loaded with a null filter, or
 * without a variable it declares, could never be answered. Completing the
 * variables first also makes the serialised form canonical, so a loader and its
 * page recognise each other's variables however each built them.
 */
export function completeVariables<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
): VariablesOf<TQuery> {
  return createOperationDescriptor(getRequest(query), variables).request
    .variables as VariablesOf<TQuery>;
}

let shellSignedIn = false;

/**
 * Records that the authenticated app shell is up, so loaders can preload
 * without waiting for a session check of their own. Called by `App`, which
 * renders only behind the sign-in gate.
 */
export function markShellSignedIn(): void {
  shellSignedIn = true;
}

/**
 * Whether route loaders may start queries.
 *
 * A loader that awaits anything delays the navigation that triggered it, so
 * once the shell is up this resolves without waiting on Amplify. Before then —
 * a cold load or a hard refresh, where preloading is worth the most, because
 * the query can run alongside the app's own startup queries rather than after
 * them — there is no navigation on screen to delay, so waiting for the session
 * check is worth it.
 *
 * Exported for tests.
 */
export async function canPreloadRouteQueries(): Promise<boolean> {
  return shellSignedIn || (await hasAuthSession());
}

/**
 * Starts fetching a route's query, for a route `loader` to return.
 *
 * Resolves to null when the query can't be started yet (see
 * `canPreloadRouteQueries`); the page then loads it on mount, as it did before
 * the route had a loader.
 *
 * @param query - The query to fetch, imported from `__generated__`: a loader
 *   lives in its own chunk so that it can run while the page's much larger
 *   chunk is still downloading, and importing the `graphql` tag from the page
 *   would pull that chunk in.
 * @param variables - Variables to fetch with. Build them with the same
 *   function the page uses, so that the page recognises them as its own and
 *   doesn't immediately load the query again.
 * @param options - Where to load into: the app's Relay environment by default,
 *   a mock in tests.
 */
export async function preloadRouteQuery<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
  options: RoutePreloadOptions = {},
): Promise<PreloadedRoute<TQuery> | null> {
  if (!(await canPreloadRouteQueries())) {
    return null;
  }

  return load<TQuery>(query, variables, options);
}

export interface RoutePreloadOptions {
  /** Defaults to the app's Relay environment; tests pass a mock. */
  environment?: IEnvironment;
}

function load<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
  { environment = RelayEnvironment }: RoutePreloadOptions,
  fetchPolicy?: FetchPolicy,
): PreloadedRoute<TQuery> {
  const completed = completeVariables<TQuery>(query, variables);

  return {
    queryRef: loadQuery<TQuery>(environment, query, completed, {
      fetchPolicy,
    }),
    variablesKey: JSON.stringify(completed),
  };
}

/**
 * How many hovered-but-not-visited queries are kept. Each holds its results in
 * the Relay store, so this is a cache size, not just a counter; a handful
 * covers a user reading down a list of links.
 */
const HOVER_PRELOAD_LIMIT = 4;

/**
 * How long a hovered-but-not-visited query stays usable. Hover intent decays
 * fast — a user who clicks does so within seconds — and a reference handed out
 * long after its fetch would render data as old as the hover, with nothing to
 * refresh it. Expired entries are swept whenever the cache is touched, and the
 * next hover fetches over the network again (see the fetch policy in
 * `preloadQueryOnHover`), so what a click lands on is never older than this.
 */
const HOVER_PRELOAD_MAX_AGE_MS = 30_000;

interface HoverPreload {
  entry: PreloadedRoute<OperationType>;
  /** When the fetch started, for `HOVER_PRELOAD_MAX_AGE_MS`. */
  fetchedAt: number;
}

// One cache for queries of every shape, so entries are only typed at the two
// boundaries below.
const hoverPreloads = new Map<string, HoverPreload>();

function isExpired({ fetchedAt }: HoverPreload): boolean {
  return Date.now() - fetchedAt > HOVER_PRELOAD_MAX_AGE_MS;
}

/**
 * Disposes every hover that has outlived `HOVER_PRELOAD_MAX_AGE_MS`. Runs
 * before each read or write of the cache: an expired entry must not be handed
 * out, and — just as important — must stop retaining its results, which the
 * store would otherwise keep serving as if they were fresh.
 */
function sweepExpiredHoverPreloads(): void {
  for (const [key, preload] of hoverPreloads) {
    if (isExpired(preload)) {
      hoverPreloads.delete(key);
      preload.entry.queryRef.dispose();
    }
  }
}

function hoverKey(query: GraphQLTaggedNode, variablesKey: string): string {
  return `${getRequest(query).params.name}:${variablesKey}`;
}

/**
 * Starts a query the user looks like they are about to need, having hovered or
 * focused a link to the page that asks for it.
 *
 * The reference is kept until that page's route loader takes it
 * (`takeHoverPreload`), until enough other links have been hovered to push it
 * out, or until it outlives `HOVER_PRELOAD_MAX_AGE_MS` — once dropped it is
 * disposed and its results become collectable.
 * Pages that have no loader still benefit: Relay joins their query to this
 * in-flight request, or reads the results it left in the store.
 *
 * Does nothing until the app shell is up — hovering means it is, so unlike a
 * route loader this never has a session to wait for.
 */
export function preloadQueryOnHover<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
  options: RoutePreloadOptions = {},
): void {
  if (!shellSignedIn) {
    return;
  }

  sweepExpiredHoverPreloads();

  const key = hoverKey(
    query,
    JSON.stringify(completeVariables<TQuery>(query, variables)),
  );

  if (hoverPreloads.has(key)) {
    // Already in flight or freshly fetched; nothing to start.
    return;
  }

  hoverPreloads.set(key, {
    // store-and-network, because an earlier fetch of this query may still be
    // in the store, and the default policy would serve that and send nothing —
    // a hover has to put a request on the wire, or the freshness the max age
    // promises never materialises. Anything the store does have still renders
    // the instant the page asks.
    entry: load<TQuery>(
      query,
      variables,
      options,
      "store-and-network",
    ) as PreloadedRoute<OperationType>,
    fetchedAt: Date.now(),
  });

  for (const [oldest, evicted] of hoverPreloads) {
    if (hoverPreloads.size <= HOVER_PRELOAD_LIMIT) {
      break;
    }
    hoverPreloads.delete(oldest);
    evicted.entry.queryRef.dispose();
  }
}

/**
 * Hands over the query `preloadQueryOnHover` started for these variables, if
 * one is still waiting. The caller owns it from here on.
 */
export function takeHoverPreload<TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
): PreloadedRoute<TQuery> | null {
  sweepExpiredHoverPreloads();

  const key = hoverKey(
    query,
    JSON.stringify(completeVariables(query, variables)),
  );
  const preload = hoverPreloads.get(key);

  if (!preload) {
    return null;
  }

  hoverPreloads.delete(key);

  if (preload.entry.queryRef.isDisposed) {
    return null;
  }

  return preload.entry as PreloadedRoute<TQuery>;
}
